import React from 'react';
import { fmt } from '../../utils';

const LOGO = process.env.PUBLIC_URL + '/rnl-logo.png';

/**
 * Print layout for a single payment voucher.
 *
 * pdfMode=true  — renders visibly (no display:none) for html2pdf capture.
 * pdfMode=false — hides via CSS, revealed only by @media print.
 */
export default function VoucherPrintView({ voucher, history, pdfMode = false }) {
  if (!voucher) return null;
  const v = voucher;
  const items = v.items || [];
  const outstanding = Number(v.net_payable || 0) - Number(v.amount_paid || 0);

  const Row = ({ label, value }) => (
    <div className="pvp-f"><span className="pvp-l">{label}</span><span className="pvp-v">{value ?? '—'}</span></div>
  );

  const printStyles = `
    .pv-print-root { display: none; }
    @media print {
      body * { visibility: hidden !important; }
      .pv-print-root, .pv-print-root * { visibility: visible !important; }
      .pv-print-root {
        display: block !important;
        position: absolute; left: 0; top: 0; width: 100%;
        padding: 0; margin: 0; background: #fff; color: #000;
        font-family: Arial, Helvetica, sans-serif;
      }
      @page { size: A4 landscape; margin: 12mm; }
      ${sharedStyles}
      .pvp-watermark {
        position: fixed; top: 50%; left: 50%;
        transform: translate(-50%, -50%);
        width: 380px; opacity: 0.06; z-index: 0;
        pointer-events: none;
      }
    }
  `;

  const pdfStyles = `
    .pv-print-root {
      display: block;
      padding: 0; margin: 0; background: #fff; color: #000;
      font-family: Arial, Helvetica, sans-serif;
      position: relative;
    }
    ${sharedStyles}
    .pvp-watermark {
      position: absolute; top: 50%; left: 50%;
      transform: translate(-50%, -50%);
      width: 420px; opacity: 0.06; z-index: 0;
      pointer-events: none;
    }
  `;

  return (
    <div className="pv-print-root" style={pdfMode ? { display: 'block', background: '#fff', color: '#000', fontFamily: 'Arial, Helvetica, sans-serif', position: 'relative' } : {}}>
      <style>{pdfMode ? pdfStyles : printStyles}</style>

      {/* Watermark */}
      <img src={LOGO} alt="" className="pvp-watermark" aria-hidden="true" />

      {/* Header */}
      <div className="pvp-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src={LOGO} alt="Rifitel" style={{ width: 42, height: 42, objectFit: 'contain', flexShrink: 0 }} />
          <div>
            <div className="pvp-title">RIFITEL NIGERIA LIMITED</div>
            <div className="pvp-sub">Payment Voucher — {v.is_advance ? 'Advance Payment' : 'Payment'}</div>
          </div>
        </div>
        <div className="pvp-no">
          {v.voucher_no || '—'}
          <div style={{ fontSize: 9, fontWeight: 400 }}>{v.voucher_date || ''}</div>
          <div style={{ fontSize: 9, fontWeight: 700 }}>{(v.status || '').toUpperCase()}</div>
        </div>
      </div>

      <div style={{ position: 'relative', zIndex: 1 }}>
        <div className="pvp-sec">A — Voucher Information</div>
        <div className="pvp-grid">
          <Row label="Voucher No." value={v.voucher_no} />
          <Row label="Manual No." value={v.manual_voucher_no} />
          <Row label="Voucher Date" value={v.voucher_date} />
          <Row label="Payment Type" value={v.payment_type} />
          <Row label="Payment Method" value={v.payment_method} />
          <Row label="Priority" value={v.priority} />
          <Row label="Currency" value={v.currency} />
          <Row label="Requisition No." value={v.requisition_no || v.purchase_request_pr_number} />
        </div>

        <div className="pvp-sec">B — Supplier</div>
        <div className="pvp-grid">
          <Row label="Supplier" value={v.supplier_name} />
          <Row label="Tax ID / TIN" value={v.supplier_tax_id} />
          <Row label="Bank" value={v.supplier_bank_name} />
          <Row label="Account Number" value={v.supplier_bank_account} />
        </div>

        <div className="pvp-sec">C — Line Items ({items.length})</div>
        <table className="pvp-t">
          <thead><tr>
            {['#', 'Description', 'Unit', 'Qty', 'Rate', 'Amount', 'Advance Alloc.', 'Ledger', 'Cost Centre', 'Project']
              .map(h => <th key={h}>{h}</th>)}
          </tr></thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={it.item_id || i}>
                <td>{i + 1}</td>
                <td>{it.description}</td>
                <td>{it.unit_of_measure || '—'}</td>
                <td className="pvp-r">{it.process_quantity}</td>
                <td className="pvp-r">{fmt(it.unit_rate)}</td>
                <td className="pvp-r">{fmt(it.line_amount)}</td>
                <td className="pvp-r">{fmt(it.advance_allocated_amount)}</td>
                <td>{it.account_ledger_name || '—'}</td>
                <td>{it.cost_centre_name || '—'}</td>
                <td>{it.project_name || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {v.is_advance && (
          <>
            <div className="pvp-sec">D — Advance Payment</div>
            <div className="pvp-grid">
              <Row label="Advance Amount" value={fmt(v.advance_amount)} />
              <Row label="Allocation Basis" value={v.advance_allocation_basis} />
              <Row label="Recovery Method" value={v.advance_recovery_method} />
              <Row label="Expected Delivery" value={v.expected_delivery_date} />
            </div>
          </>
        )}

        <div className="pvp-sec">{v.is_advance ? 'E' : 'D'} — Payment Summary</div>
        <div className="pvp-grid">
          <Row label="Subtotal" value={fmt(v.subtotal_amount)} />
          <Row label="Discount" value={fmt(v.less_discount)} />
          <Row label="Retention" value={fmt(v.less_retention)} />
          <Row label="Other Additions" value={fmt(v.other_additions)} />
          <Row label="Gross Amount" value={fmt(v.gross_amount)} />
          <Row label={`VAT (${v.vat_percent}%)`} value={fmt(v.vat_amount)} />
          <Row label={`WHT (${v.wht_percent}%)`} value={fmt(v.wht_amount)} />
          <Row label="Withheld VAT" value={fmt(v.withheld_vat)} />
          <Row label="Other Deductions" value={fmt(v.other_deductions)} />
          <Row label="Advance Recovered" value={fmt(v.advance_recovered_amount)} />
          <Row label="Amount Payable" value={fmt(v.amount_payable)} />
          <Row label="Outstanding" value={fmt(outstanding)} />
        </div>

        <div className="pvp-net">
          <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase' }}>Net Payable</span>
          <span className="pvp-net-v">{fmt(v.net_payable)}</span>
        </div>

        {v.narration && (
          <>
            <div className="pvp-sec">Narration</div>
            <div style={{ fontSize: 9.5 }}>{v.narration}</div>
          </>
        )}

        {(history || []).length > 0 && (
          <>
            <div className="pvp-sec">Approval Trail</div>
            <table className="pvp-t">
              <thead><tr>{['Date', 'Action', 'User', 'Step', 'Comment'].map(h => <th key={h}>{h}</th>)}</tr></thead>
              <tbody>
                {history.map((h, i) => (
                  <tr key={h.log_id || i}>
                    <td>{(h.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                    <td>{h.action}</td>
                    <td>{h.actor_name || '—'}</td>
                    <td>{h.step_name || '—'}</td>
                    <td>{h.comment || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <div className="pvp-sign">
          <div>Prepared By — {v.created_by_name || ''}</div>
          <div>Checked By — {v.posted_by_name || ''}</div>
          <div>Approved By — {v.approved_by_name || ''}</div>
          <div>Released By — {v.released_by_name || ''}</div>
        </div>

        <div className="pvp-foot">
          {v.released_to_expense_register
            ? `Released to the Expense Register${v.released_at ? ` on ${String(v.released_at).slice(0, 10)}` : ''}.`
            : 'Not yet released to the Expense Register.'}
          {'  ·  '}Generated {new Date().toLocaleString()}
        </div>
      </div>
    </div>
  );
}

const sharedStyles = `
  .pvp-title { font-size: 17px; font-weight: 800; letter-spacing: .5px; }
  .pvp-sub { font-size: 10px; color: #444; margin-top: 2px; }
  .pvp-head { display: flex; justify-content: space-between;
              align-items: flex-start; border-bottom: 2px solid #000;
              padding-bottom: 8px; margin-bottom: 10px; }
  .pvp-no { font-size: 13px; font-weight: 800; text-align: right; }
  .pvp-sec { font-size: 10px; font-weight: 800; text-transform: uppercase;
             letter-spacing: .5px; margin: 10px 0 5px; border-bottom: 1px solid #000; }
  .pvp-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 4px 10px; }
  .pvp-f { border: 1px solid #999; padding: 3px 5px; }
  .pvp-l { display: block; font-size: 7.5px; text-transform: uppercase;
           letter-spacing: .3px; color: #555; }
  .pvp-v { display: block; font-size: 10px; font-weight: 700; }
  .pvp-t { width: 100%; border-collapse: collapse; margin-top: 4px; }
  .pvp-t th, .pvp-t td { border: 1px solid #666; padding: 3px 4px;
                         font-size: 8.5px; text-align: left; }
  .pvp-t th { background: #e8e8e8; font-weight: 800; text-transform: uppercase;
              font-size: 7.5px; }
  .pvp-r { text-align: right !important; }
  .pvp-net { border: 2px solid #000; padding: 6px 10px; margin-top: 8px;
             display: flex; justify-content: space-between; align-items: center; }
  .pvp-net-v { font-size: 15px; font-weight: 800; }
  .pvp-sign { display: grid; grid-template-columns: repeat(4, 1fr);
              gap: 20px; margin-top: 26px; }
  .pvp-sign div { border-top: 1px solid #000; padding-top: 3px;
                  font-size: 8.5px; text-transform: uppercase; }
  .pvp-foot { margin-top: 14px; font-size: 7.5px; color: #555;
              border-top: 1px solid #999; padding-top: 4px; }
  .pvp-t tr { page-break-inside: avoid; }
`;
